import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import TagsCollection from '@components/Chat/TagsCollection';
import { Box, Text } from '@components/Restyle';
import AcceptDeclineButtonsNew from '@components/shared/Button/AcceptDeclineButtonsNew';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { Tag } from '@components/shared/Tags/Tag';
import { Message } from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { AsyncStorage } from '@utils/storage';
import { generateTagCollections } from '@utils/tagCollectionService';
import { resolveTaskMemberActivityAction } from '@utils/userRequestClassification';

type TaskMemberJoinRequestProps = {
  message: Message;
  showAcceptDecline?: boolean;
  filterVal?: string;
  doNotUseGlobalSearch?: boolean;
  isActivityItem?: boolean;
};

export const TaskMemberJoinRequest: React.FC<TaskMemberJoinRequestProps> = ({
  message,
  showAcceptDecline,
  filterVal,
  doNotUseGlobalSearch,
  isActivityItem = false,
}) => {
  const { me } = useMe();
  const { t } = useTranslation('format');
  const { attachments, userRequest, isSender, authorFirstName, authorName } =
    message;
  const tagsByAuthorCollections = generateTagCollections(attachments || []);
  const firstTask = tagsByAuthorCollections[0]?.[0]?.tasks[0];
  const taskId = firstTask?.id ?? '';
  const isPending = userRequest?.status === 'pending';
  const isRejected = userRequest?.status === 'rejected';
  const [showAcceptDeclineButtons, setShowAcceptDeclineButtons] =
    useState(showAcceptDecline);
  const isJoinTask = userRequest?.user?.id === me?.id;
  // TA-10744 / TA-10745: after an expired-invite registration the invitee is
  // waiting on approval and the inviter (this message's sender) is the one who
  // decides — canBeReviewed says which, isSender alone gets it backwards.
  const memberActivityAction = resolveTaskMemberActivityAction({
    status: userRequest?.status,
    isInvitee: isJoinTask,
    isSender,
    canBeReviewed: userRequest?.canBeReviewed,
  });
  const canActOnRequest =
    memberActivityAction === 'join' || memberActivityAction === 'review';
  const { navigateToTask } = useAppNavigation();
  const taskDeleted = tagsByAuthorCollections.some((tagCollections) =>
    tagCollections.some((tag) =>
      tag.tasks.some((task) => task.deletedAt && task.deletedAt !== null)
    )
  );

  const assigneeName = userRequest?.user?.name ?? '';
  const assigneeFirstName = assigneeName.split(' ')[0];
  // authorFirstName is nullable — fall back to first word of authorName
  const inviterFirstName = authorFirstName ?? authorName?.split(' ')[0] ?? '';

  const getMessage = () => {
    if (isJoinTask) {
      const pendingMessageKey =
        memberActivityAction === 'request'
          ? 'models:chat.askToJoinThisTask'
          : 'models:chat.youHaveBeenInvitedToJoinThisTask';
      if (isPending) return t(pendingMessageKey);
      if (isRejected) return t('models:chat.youHaveBeenDeclinedFromThisTask');
      return t('models:chat.inviterAddedYouToThisTask', {
        name: inviterFirstName,
      });
    } else {
      if (isPending && isSender && memberActivityAction !== 'review') {
        return t('models:chat.requestNameToJoinThisTask', {
          name: assigneeName,
        });
      }
      if (isPending) {
        return t('models:chat.nameRequestToJoinThisTask', {
          name: assigneeName,
        });
      }
      if (isRejected) return t('models:chat.declinedFromThisTask');
      return t('models:chat.nameHasBeenAssignedATask', { name: assigneeName });
    }
  };

  // Single derived flag — showWorkingOn and showViewTask share identical condition
  const showTaskDetails =
    !isPending && !isRejected && !!firstTask && !isActivityItem;

  let messageMarginBottom: 'xxs' | 'xs' | 's' = 's';
  if (showTaskDetails) messageMarginBottom = 'xxs';
  else if (isActivityItem) messageMarginBottom = 'xs';

  // Resolved invite/assignment with a task to link to — Figma-style "TASK ASSIGNED" card.
  if (showTaskDetails) {
    return (
      <Box
        borderColor='grey02'
        borderWidth={1}
        borderRadius='xs'
        marginVertical='s'
        backgroundColor='white'
        padding='s'>
        <Box
          mb='s'
          alignSelf='flex-start'
          height={28}
          backgroundColor='greenSecondaryMild'
          borderRadius='m'
          px='s'
          alignItems='center'
          justifyContent='center'>
          <Text
            variant='metadataSemiBold'
            lineHeight={12}
            color='textSecondary'>
            {t('models:chat.taskAssignedBadge')}
          </Text>
        </Box>
        <Text
          variant='labelEmphasized'
          color='textPrimary'
          marginBottom={messageMarginBottom}>
          {getMessage()}
        </Text>
        <Text variant='webBodySecondary' color='textSecondary' marginBottom='s'>
          {isJoinTask
            ? `${t('models:chat.onboarding.memberJoined.youreNowWorkingOn')} `
            : `${assigneeFirstName} ${t(
                'models:chat.onboarding.memberJoined.workingOn'
              )} `}
          <Text variant='labelEmphasized14' color='greenSecondary'>
            {firstTask.name}
          </Text>
        </Text>
        <Box alignSelf='flex-start' flexDirection='row'>
          <Button
            borderColor='black'
            backgroundColor='black'
            variant='create'
            isSmall
            width={140}
            onPress={() => navigateToTask(firstTask)}>
            <Text variant='labelSmall' color='white'>
              {t('models:chat.onboarding.memberJoined.viewTask')}
            </Text>
          </Button>
          {!isJoinTask && (
            <Button
              borderColor='black'
              borderWidth={1}
              backgroundColor='white'
              variant='create'
              isSmall
              width={140}
              height={48}
              marginLeft='m'
              onPress={async () => {
                if (!firstTask) return;
                await AsyncStorage.setItem('TaskWebPanelOpenAssignee', '1');
                navigateToTask(firstTask);
              }}>
              <Text variant='labelSmall' color='black'>
                {t('models:chat.onboarding.memberJoined.assignMore')}
              </Text>
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // Pending / rejected / activity-item states — Task Request card, no "TASK ASSIGNED" badge.
  return (
    <Box
      borderColor='grey02'
      borderWidth={1}
      borderRadius='xs'
      marginVertical={isActivityItem ? undefined : 's'}
      backgroundColor='white'>
      <Tag
        label={t('models:chat.memberActivity')}
        labelVariant='metadata'
        variant='joinTask'
        prefixMarginRight='xxs'
        prefix={<Icon name='Users' variant='s' color='magentaDark' />}
        iconWidth={14.22}
        iconHeight={14.22}
        padding='xs'
        borderTopLeftRadius='xs'
        borderTopRightRadius='xs'
        borderBottomLeftRadius='none'
        borderBottomRightRadius='none'
      />
      <Text
        variant='webBodySecondary'
        color='textSecondary'
        marginHorizontal='xs'
        marginTop='xs'
        marginBottom={isActivityItem ? 'xs' : undefined}>
        {getMessage()}
      </Text>
      {!isActivityItem &&
        tagsByAuthorCollections.map((tagCollections) => {
          return tagCollections.map((item) => {
            const { project, tasks, author } = item;

            return (
              <Box
                key={project?.id}
                paddingRight={taskDeleted ? 'xs' : undefined}>
                <TagsCollection
                  doNotUseGlobalSearch={doNotUseGlobalSearch}
                  filterVal={filterVal ?? ''}
                  type='MemberActivity'
                  tagsCollection={{ project, tasks, author }}
                  showAuthor={false}
                  borderless={true}
                  showProject={false}
                  isJoinRequestPending={(isPending || isRejected) && isJoinTask}
                  isRow={true}
                  isActivityItem={isActivityItem}
                  hasPaddingLeft
                />
              </Box>
            );
          });
        })}
      {showAcceptDeclineButtons && canActOnRequest && !isActivityItem && (
        <Box
          flexDirection='row'
          alignItems='center'
          marginHorizontal='xs'
          paddingTop={isJoinTask ? undefined : 'xxs'}
          paddingBottom='xs'>
          {!taskDeleted && (
            <AcceptDeclineButtonsNew
              isJoinTask={memberActivityAction === 'join'}
              suffix={true}
              requestId={userRequest?.id}
              taskId={taskId}
              toggleAcceptDecline={() => {
                setShowAcceptDeclineButtons(isPending);
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
